/**
 * @fileoverview vue コンポーネント（クラス）へ標準的に mixin しておきたいユーティリティメソッド群。
 *               これは Vue Plugin であり、 Vue.use() することで利用可能となる。
 *               別途 export している utils オブジェクトを import すれば、vue コンポーネントによらない使用も可能。
 */

import _ from 'underscore'
import $ from 'jquery'
// import moment from 'moment'


export const utils = {
  /**
   * パス文字列あるいは URL 文字列から完全 URL を得る
   * @param {string} url    パス文字列あるいは URL 文字列
   * @return {string}
   */
  rel2absURL (url) {
    const anchor = document.createElement('a')
    anchor.href = url
    return anchor.href
  },

  /**
   * 数値の桁数が想定よりすくないときに任意の文字で足りない桁を埋める
   * @param {number} num    処理対象の数値。
   * @param {string} fill   埋める文字列。"00" とか。
   * @return {string} 処理済み文字列
   */
  fill (num, fill) {
    num = Number(num) || 0
    fill = String(fill)
    return (fill + num).substr(-1 * fill.length)
  },

  /**
   * 小文字にする
   * @param {string} str    処理対象テキスト
   * @return {string} 処理結果の文字列
   */
  lc (str) {
    return (str + '').toLowerCase()
  },

  /**
   * 大文字にする
   * @param {string} str    処理対象テキスト
   * @return {string} 処理結果の文字列
   */
  uc (str) {
    return (str + '').toUpperCase()
  },

  /**
   * 先頭の文字を大文字にする
   * @param {string} str    処理対象テキスト
   * @return {string} 処理結果の文字列
   */
  cap (str) {
    str += ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  },

  /**
   * @see {.cap}
   */
  capitalize () {
    return this.cap.apply(this, arguments)
  },

  /**
   * 文字列全体を引用符で囲う
   * @param {string} str            処理対象テキスト
   * @param {string} [quote='"']    引用符にする文字。無指定時は "
   * @return {string} 処理結果の文字列
   */
  quote (str, quote) {
    _.isString(quote) || (quote = '"')
    return `${quote}${str}${quote}`
  },

  /**
   * 数字（文字列）に数詞を追加する
   * Vue.js (～v1.0.x) の "pluralize" フィルタに似ているが、同フィルタは「値を数詞に“変換するのみ”」であるのが異なる。
   * @param {string|number} num         数字
   * @param {...string}     var_args    数詞。指定方法は Vue の "pluralize" フィルタと同じ。1つ以上必要。
   * @return {string} 処理結果の文字列
   */
  numeral (num, var_args) {
    // 数詞を得る
    // Refer to: https://github.com/freearhey/vue2-filters/blob/master/src/other/pluralize.js
    const args = _.rest(arguments)
    const numeral = args.length > 1
      ? (args[num % 10 - 1] || args[args.length - 1])
      : (args[0] + (num === 1 ? '' : 's'))

    return `${num} ${numeral}`
  },

  /**
   * 日時情報を整形する
   * @param {string|number|Date} date           日時情報 (正当な日時文字列 (ex: ISO-8601) or エポック秒 or Date オブジェクト)
   * @param {string}             [format='']    フォーマット文字列
   * @return {string} 処理結果の文字列
   * @see フォーマット文字列→ http://momentjs.com/docs/#/displaying/format/
   */
  // datetime (date, format) {
  //   return !date ? '' : moment(date).format(_.isString(format) ? format : '')
  // },

  /**
   * 特定キーワードより手前の部分を消す
   * @param {string} val    処理対象テキスト
   * @param {string} key    検索キーワード
   * @return {string} 処理結果の文字列
   */
  omitBefore (val, key) {
    return !key ? val : val.split(key).reverse().slice(0, 1).reverse().join(key)
  },

  /**
   * 特定キーワードより後ろの部分を消す
   * @param {string} val    処理対象テキスト
   * @param {string} key    検索キーワード
   * @return {string} 処理結果の文字列
   */
  omitAfter (val, key) {
    return !key ? val : val.split(key).slice(0, 1).join(key)
  },

  /**
   * 特定キーワードより手前の部分を週出する
   * @param {string} val         処理対象テキスト
   * @param {string} key         検索キーワード
   * @param {string} [alt=""]    何も抽出されないときの代替文字列
   * @return {string} 抽出された文字列
   */
  lefthand (val, key, alt) {
    return !key ? '' : val.split(key)[0] || alt || ''
  },

  /**
   * 特定キーワードより後ろの部分を週出する
   * @param {string} val         処理対象テキスト
   * @param {string} key         検索キーワード
   * @param {string} [alt=""]    何も抽出されないときの代替文字列
   * @return {string} 抽出された文字列
   */
  righthand (val, key, alt) {
    return !key ? '' : val.split(key).splice(1).join(key) || alt || ''
  },

  /**
   * 配列の要素数、または文字列の文字数を得る
   * @param {string|Array} val    処理対象の値
   * @return {number} 配列要素数、または文字数
   */
  count (val) {
    return (_.isString(val) || _.isArray(val)) ? val.length : NaN
  },

  /**
   * 引数それぞれを真偽値に変換した時、すべて true なら true を返す
   * @see http://underscorejs.org/#every
   * @param {*} var_args
   * @return {boolean}
   */
  every () {
    return _.every(arguments)
  },

  /**
   * 引数それぞれを真偽値に変換した時、そのうち1つでも true なら true を返す
   * @see http://underscorejs.org/#some
   * @param {*} var_args
   * @return {boolean}
   */
  some () {
    return _.some(arguments)
  },

  /**
   * 配列に入っている連想配列群それぞれについて、指定のプロパティのみ残す
   * @see http://underscorejs.org/#pick
   * @param {Object[]}  list
   * @param {...string} var_args
   * @return {Object[]} 処理済みの配列
   */
  pick (list, var_args) {
    const keys   = _.rest(arguments)
    const picker = _.partial(_.pick, _, keys)
    return _.map(list, picker)
  },

  /**
   * 配列に入っている連想配列群それぞれについて、指定のプロパティを削除する
   * @see http://underscorejs.org/#omit
   * @param {Object[]}  list
   * @param {...string} var_args
   * @return {Object[]} 処理済みの配列
   */
  omit (list, var_args) {
    const keys    = _.rest(arguments)
    const omitter = _.partial(_.omit, _, keys)
    return _.map(list, omitter)
  },

  /**
   * 配列に入っている連想配列のうち、指定プロパティの値が指定値のもののみ残す。
   * @param {{[key:string]:any}[]} arr            連想配列で構成された配列
   * @param {{[key:string]:any}}   query          key の値が value ものを探す
   * @param {boolean}              [sensitive]    true のとき文字列値を case sensitive で検索 ( _.where() と同じ結果になる)
   * @return {{[key:string]: any}[]} 該当の連想配列のみに内容が絞られた配列
   *
   * @example only(the_list, { prop_name: 'foo_value' })
   *   この例は _.where(the_list, { prop_name: 'foo_value' }) とほぼ同じ結果を得る。
   *   違いは、値が文字列のとき、case insensitive な比較をすること。
   *
   * @example only(the_list, { prop_name: 'foo_value' }, true)
   *   この例は _.where(the_list, { prop_name: 'foo_value' }) と同じ結果を得る。
   */
  only (arr, query, sensitive) {
    const lc  = _val => (!sensitive && _.isString(_val)) ? _val.toLowerCase() : _val
    const key = Object.keys(query)[0]
    const val = Object.values(query)[0]

    return _.chain(arr)
      .pluck(key)
      .map((_val, _idx) => lc(_val) === lc(val) ? arr[_idx] : null)
      .compact()
      .value()
  },

  /**
   * 配列に入っている連想配列のうち、指定プロパティの値が指定値のものを除外する。 "only" フィルタの逆。
   * @param {{[key:string]:any}[]} arr            連想配列で構成された配列
   * @param {{[key:string]:any}}   query          key の値が value ものを探す
   * @param {boolean}              [sensitive]    true のとき文字列値を case sensitive で検索
   * @return {Object[]} 該当の連想配列が除外された配列
   */
  without (arr, query, sensitive) {
    const found = this.only(arr, query, sensitive)
    return _.difference(arr, found)
  },

  /**
   * 配列や文字列の並びを反転する
   * @param {string|array} val
   * @return {string|array}
   */
  reverse (val) {
    return _.isArray(val)
      ? _.clone(val).reverse()
      : String(val).split('').reverse().join('')
  },

  /**
    * 指定座標までページスクロール
    * @param {{left:number, top:number}} pos               目標地点。ページ左上原点の座標 (px)
    * @param {number}                    [duration=500]    duration (msec)
    */
  scrollTo (pos, duration) {
    const prop = {
      scrollLeft: _.isNumber(pos.left) ? pos.left : '',
      scrollTop : _.isNumber(pos.top ) ? pos.top  : '',
    }
    duration = _.isNumber(duration) ? Math.max(0, duration) : 500
    $('html, body').animate(prop, duration)
  },

  /**
   * Model データの中の方々につけられた Vue.js 由来の getter/setter などを消去したものを返す。
   * @param {Object} model    処理対象の Model データオブジェクト。
   * @return {Object}
   */
  cleanUp (model) {
    return _.isObject(model)
      ? JSON.parse(JSON.stringify(model))
      : model
  },

  /**
   * @see .cleanUp
   */
  clone () {
    return this.cleanUp.apply(this, arguments)
  },

  /**
   * なにもしない
   * @see http://underscorejs.org/#noop
   */
  noop () {
    return _.noop()
  },
}


export default {
  install: Vue => Vue.mixin({ methods: utils })
}

